import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { modalState } from '../../atoms/ModalState';
import { pageLoadingState } from '../../atoms/PageState';
import { currentLocationState, peopleState, userActionState } from '../../atoms/QueryState';
import { allShiftsState } from '../../atoms/ShiftState';
import { i18Prefix } from '../../i18Constants';
import '../../i18n';
import { editShifts } from '../../services/shifts';
import {
  AssignmentIssue,
  AssignmentIssueReason,
  ParseResult,
  ValidationResult,
  parseAssignmentCsv,
  validateAssignments,
} from '../../utilities/bulkAssignCsv';
import {
  colorPalette,
  PrimaryActionButton,
  SecondaryActionButton,
} from '../../utilities/Styles';
import { closeModalTestId } from '../../utilities/TestIds';
import { modals, pageState, userActions } from '../../utilities/Variables';

const bulkAssignModalTestId = 'bulkAssignModal';
const bulkAssignUploadTestId = 'bulkAssignUpload';
const bulkAssignSubmitTestId = 'bulkAssignSubmit';
const bulkAssignCancelTestId = 'bulkAssignCancel';
const bulkAssignPublishTestId = 'bulkAssignPublish';
const bulkAssignFileErrorTestId = 'bulkAssignFileError';
const bulkAssignResultsTestId = 'bulkAssignResults';
const templateFileName = 'shift-driver-assignments-template.csv';

interface AssignmentFailure {
  shiftId: number;
  driverId: number;
  message?: string;
}

interface SubmitResults {
  success: number;
  failures: AssignmentFailure[];
}

// One PATCH /v2/manager/shifts update. Assign-only carries no publish fields.
export interface ShiftUpdate {
  id: number;
  assignedPersonId: number;
  locationId: any;
  isPublished?: boolean;
  publishedTime?: string | null;
}

// Large uploads are sent in batches so we never fire one huge PATCH.
export const BULK_ASSIGN_BATCH_SIZE = 100;

// Split a list into batches of at most `size` (the last batch may be smaller).
// Returns [] for an empty list so callers never PATCH an empty array.
export const chunk = <T,>(items: T[], size: number): T[][] => {
  const batches: T[][] = [];
  const step = size > 0 ? size : items.length || 1;
  for (let i = 0; i < items.length; i += step) {
    batches.push(items.slice(i, i + step));
  }
  return batches;
};

// Build the PATCH payload. Assign-only by default; when `publish` is set each
// row is also published with the shared timestamp.
export const buildAssignPayload = (
  rows: { shiftId: number; driverId: number }[],
  opts: { locationId: any; publish: boolean; now: string | null },
): ShiftUpdate[] =>
  rows.map((row) => ({
    id: row.shiftId,
    assignedPersonId: row.driverId,
    locationId: opts.locationId,
    ...(opts.publish ? { isPublished: true, publishedTime: opts.now } : {}),
  }));

// Map one batch's response back to per-row success/failure. A missing entry or
// an explicit { success: false } is a failure; anything else counts as success.
export const summarizeEditResults = (batch: ShiftUpdate[], response: any): SubmitResults => {
  let success = 0;
  const failures: AssignmentFailure[] = [];
  batch.forEach((item, index) => {
    const entry = Array.isArray(response) ? response[index] : undefined;
    if (entry && entry.success !== false) {
      success += 1;
    } else {
      failures.push({ shiftId: item.id, driverId: item.assignedPersonId, message: entry?.message });
    }
  });
  return { success, failures };
};

const BulkAssignModalBody = (props: { open?: boolean; mockFile?: File }) => {
  const { t } = useTranslation();

  const [displayModal, setDisplayModal] = useRecoilState(modalState);
  const [pageLoading, setPageLoading] = useRecoilState(pageLoadingState);
  const [userAction, setUserAction] = useRecoilState(userActionState);
  const currentLocation = useRecoilValue(currentLocationState);
  const people = useRecoilValue(peopleState) as any[];
  const allShifts = useRecoilValue(allShiftsState) as any[];

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [parseResult, setParseResult] = useState<ParseResult | undefined>(undefined);
  const [validation, setValidation] = useState<ValidationResult | undefined>(undefined);
  const [publishToo, setPublishToo] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<SubmitResults | undefined>(undefined);
  const [apiError, setApiError] = useState<string | undefined>(undefined);

  // Authoritative driver ids for the location's loaded people. An empty set means
  // "no valid drivers" and the validator fails every row (never fail-open).
  const driverIds = useMemo(
    () => new Set<number>((people || []).map((person) => Number(person.id))),
    [people],
  );
  // Shift ids we have actually loaded (the current scheduler window). Assignment
  // status is only trustworthy for these; anything else is flagged NotInView.
  const loadedShiftIds = useMemo(
    () => new Set<number>((allShifts || []).map((shift) => Number(shift.id))),
    [allShifts],
  );
  // Loaded shifts that already have a driver — skipped, never overwritten.
  const assignedShiftIds = useMemo(
    () =>
      new Set<number>(
        (allShifts || [])
          .filter((shift) => shift.assignedPersonId !== null && shift.assignedPersonId !== undefined)
          .map((shift) => Number(shift.id)),
      ),
    [allShifts],
  );
  // Only trust driverIds / loadedShiftIds once the scheduler has finished loading
  // people + shifts. Until then, block upload + submit so we never validate (or
  // assign) against a half-loaded location.
  const dataReady = pageLoading === pageState.done && !!currentLocation?.id;

  const genericError = t(`${i18Prefix}.api_failure`);

  const reasonText = (reason: AssignmentIssueReason) => {
    switch (reason) {
      case AssignmentIssueReason.MissingColumns:
        return t(`${i18Prefix}.bulk_assign_reason_missing_columns`);
      case AssignmentIssueReason.MissingFields:
        return t(`${i18Prefix}.bulk_assign_reason_missing_fields`);
      case AssignmentIssueReason.NotANumber:
        return t(`${i18Prefix}.bulk_assign_reason_not_a_number`);
      case AssignmentIssueReason.Duplicate:
        return t(`${i18Prefix}.bulk_assign_reason_duplicate`);
      case AssignmentIssueReason.InvalidDriver:
        return t(`${i18Prefix}.bulk_assign_reason_invalid_driver`);
      case AssignmentIssueReason.AlreadyAssigned:
        return t(`${i18Prefix}.bulk_assign_reason_already_assigned`);
      case AssignmentIssueReason.NotInView:
        return t(`${i18Prefix}.bulk_assign_reason_not_in_view`);
      default:
        return reason;
    }
  };

  const resetFileState = () => {
    setFileName(undefined);
    setParseResult(undefined);
    setValidation(undefined);
    setResults(undefined);
    setApiError(undefined);
    setPublishToo(false);
  };

  const handleClose = () => {
    resetFileState();
    setSubmitting(false);
    setDisplayModal({ open: false, modal: modals.bulkAssignModal, modalObject: {} });
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragFinish = (event: any) => {
    event.preventDefault();
    setDragging(false);
  };

  const acceptFile = async (files: File[]) => {
    resetFileState();
    const file = files?.[0];
    if (!file) return;
    if (!dataReady) {
      setApiError(t(`${i18Prefix}.bulk_assign_not_ready`));
      return;
    }
    if (!file.name || !file.name.toLowerCase().includes('.csv')) {
      setApiError(t(`${i18Prefix}.invalid_file_type`));
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseAssignmentCsv(text);
      setFileName(file.name);
      setParseResult(parsed);
      setValidation(
        parsed.fatal
          ? { toAssign: [], issues: [] }
          : validateAssignments(parsed.rows, { driverIds, loadedShiftIds, assignedShiftIds }),
      );
    } catch (err: any) {
      console.log(err);
      setApiError(err?.message || genericError);
    }
  };

  // NOTE: for testing — inject a CSV file directly, bypassing the drag/drop input
  // (jsdom has no reliable File.text()/Dropzone plumbing).
  useEffect(() => {
    if (props.mockFile) acceptFile([props.mockFile]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadTemplate = () => {
    const blob = new Blob(['shift_id,driver_id\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = templateFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!parseResult || parseResult.fatal) return;
    if (!dataReady) {
      setApiError(t(`${i18Prefix}.bulk_assign_not_ready`));
      return;
    }

    // Re-run validation against the freshest data at submit time. The loaded
    // window / people can change between file-accept and submit (auto-refresh,
    // location change), so a shift that is now assigned or no longer in view must
    // be re-skipped here — never blindly overwritten.
    //
    // LIMITATION (not atomic): this "never overwrite" guard is best-effort and
    // client-side — it reads the locally cached allShiftsState, which can still be
    // stale relative to the server at PATCH time (PATCH /v2/manager/shifts allows
    // reassignment). A hard guarantee needs a server-side conditional update that
    // rejects shifts whose assignment changed, mapped back to row failures.
    // Tracked as a Delivery Tech KTLO follow-up (see PR discussion).
    const fresh = validateAssignments(parseResult.rows, {
      driverIds,
      loadedShiftIds,
      assignedShiftIds,
    });
    setValidation(fresh);
    if (fresh.toAssign.length < 1) {
      setApiError(t(`${i18Prefix}.bulk_assign_nothing_to_submit`));
      return;
    }

    setSubmitting(true);
    setApiError(undefined);
    try {
      const now = DateTime.now().toUTC().toISO();
      const payload = buildAssignPayload(fresh.toAssign, {
        locationId: currentLocation.id,
        publish: publishToo,
        now,
      });

      // Send in batches so a large upload is not one huge PATCH. A failed batch
      // is recorded as row failures rather than discarding the other batches.
      let success = 0;
      const failures: AssignmentFailure[] = [];
      const batches = chunk(payload, BULK_ASSIGN_BATCH_SIZE);
      for (let b = 0; b < batches.length; b += 1) {
        const batch = batches[b];
        try {
          const response = await editShifts(batch);
          const summary = summarizeEditResults(batch, response);
          success += summary.success;
          failures.push(...summary.failures);
        } catch (batchErr: any) {
          batch.forEach((item) =>
            failures.push({
              shiftId: item.id,
              driverId: item.assignedPersonId,
              message: batchErr?.message || genericError,
            }),
          );
        }
      }

      setResults({ success, failures });
      setSubmitting(false);

      // Refresh the scheduler behind the modal if anything changed.
      if (success > 0) {
        setPageLoading(pageState.loading);
        setUserAction({
          userAction: userActions.bulkAssign,
          actionCount: userAction.actionCount + 1,
        });
      }
    } catch (err: any) {
      console.log(err);
      setSubmitting(false);
      setApiError(err?.message || genericError);
    }
  };

  const allIssues: AssignmentIssue[] = useMemo(() => {
    if (!parseResult) return [];
    return [...parseResult.issues, ...(validation?.issues || [])].sort((a, b) => a.line - b.line);
  }, [parseResult, validation]);

  const readyCount = validation?.toAssign.length ?? 0;

  const renderDropzone = () => (
    <Box>
      <Box style={{ marginBottom: 8, color: colorPalette.gray6, fontSize: 13 }}>
        {t(`${i18Prefix}.bulk_assign_instructions`)}
      </Box>
      {!dataReady && (
        <Box
          data-testid={bulkAssignFileErrorTestId}
          style={{ marginBottom: 8, color: colorPalette.warningRed, fontSize: 13 }}
        >
          {t(`${i18Prefix}.bulk_assign_not_ready`)}
        </Box>
      )}
      <Dropzone onDrop={(acceptedFiles) => acceptFile(acceptedFiles as File[])}>
        {({ getRootProps, getInputProps }) => (
          <>
            <input {...getInputProps({ accept: '.csv' })} />
            <Tooltip title={<h3>{t(`${i18Prefix}.drag_to_upload`)}</h3>} placement='top'>
              <PrimaryActionButton
                {...getRootProps({
                  component: 'label',
                  variant: 'contained',
                  onDragOver: handleDragOver,
                  onDragLeave: handleDragFinish,
                  onDrop: handleDragFinish,
                })}
                data-testid={bulkAssignUploadTestId}
                style={{
                  width: '100%',
                  border: `2px dashed ${dragging ? colorPalette.successGreen : colorPalette.gray4}`,
                }}
              >
                {t(`${i18Prefix}.bulk_assign_upload`)}
              </PrimaryActionButton>
            </Tooltip>
          </>
        )}
      </Dropzone>
      <Box style={{ marginTop: 10 }}>
        <Link component='button' variant='body2' onClick={downloadTemplate}>
          {t(`${i18Prefix}.bulk_assign_download_template`)}
        </Link>
      </Box>
    </Box>
  );

  const renderIssues = () =>
    allIssues.length > 0 && (
      <Box style={{ marginTop: 12 }}>
        <Box style={{ fontWeight: 700, marginBottom: 4 }}>
          {t(`${i18Prefix}.bulk_assign_issues_title`, { count: allIssues.length })}
        </Box>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{t(`${i18Prefix}.bulk_assign_line`)}</TableCell>
              <TableCell>{t(`${i18Prefix}.bulk_assign_shift`)}</TableCell>
              <TableCell>{t(`${i18Prefix}.driver`)}</TableCell>
              <TableCell>{t(`${i18Prefix}.bulk_assign_reason`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allIssues.map((issue) => (
              <TableRow key={`issue-${issue.line}-${issue.reason}`}>
                <TableCell>{issue.line}</TableCell>
                <TableCell>{issue.shiftId || '-'}</TableCell>
                <TableCell>{issue.driverId || '-'}</TableCell>
                <TableCell style={{ color: colorPalette.warningRed }}>
                  {reasonText(issue.reason)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const renderPreview = () => {
    if (!parseResult) return null;
    if (parseResult.fatal) {
      return (
        <Box style={{ color: colorPalette.warningRed, marginTop: 12 }}>
          {reasonText(parseResult.fatal)}
        </Box>
      );
    }
    return (
      <Box style={{ marginTop: 8 }}>
        <Box>
          {t(`${i18Prefix}.bulk_assign_selected_file`)}: <strong>{fileName}</strong>
        </Box>
        <Box style={{ marginTop: 6, color: colorPalette.successGreen, fontWeight: 700 }}>
          {t(`${i18Prefix}.bulk_assign_rows_ready`, { count: readyCount })}
        </Box>
        {renderIssues()}
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              color='primary'
              checked={publishToo}
              onChange={(event) => setPublishToo(event.target.checked)}
              data-testid={bulkAssignPublishTestId}
              name='publishToo'
            />
          }
          label={t(`${i18Prefix}.bulk_assign_publish`)}
        />
      </Box>
    );
  };

  const renderResults = () =>
    results && (
      <Box data-testid={bulkAssignResultsTestId} style={{ marginTop: 8 }}>
        <Box style={{ color: colorPalette.successGreen, fontWeight: 700 }}>
          {t(`${i18Prefix}.bulk_assign_results`, {
            success: results.success,
            failure: results.failures.length,
          })}
        </Box>
        {results.failures.length > 0 && (
          <Table size='small' style={{ marginTop: 8 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t(`${i18Prefix}.bulk_assign_shift`)}</TableCell>
                <TableCell>{t(`${i18Prefix}.driver`)}</TableCell>
                <TableCell>{t(`${i18Prefix}.bulk_assign_reason`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.failures.map((failure) => (
                <TableRow key={`failure-${failure.shiftId}-${failure.driverId}`}>
                  <TableCell>{failure.shiftId}</TableCell>
                  <TableCell>{failure.driverId}</TableCell>
                  <TableCell style={{ color: colorPalette.warningRed }}>
                    {failure.message || genericError}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );

  const canSubmit = !submitting && !results && dataReady && readyCount > 0;

  return (
    <Dialog
      open={props.open || displayModal.open}
      onClose={handleClose}
      aria-labelledby='bulk-assign-dialog-title'
      maxWidth='sm'
      fullWidth
      data-testid={bulkAssignModalTestId}
    >
      <DialogTitle id='bulk-assign-dialog-title' style={{ backgroundColor: colorPalette.gray2 }}>
        {t(`${i18Prefix}.bulk_assign_title`)}
        <Button
          onClick={handleClose}
          aria-label={t(`${i18Prefix}.close`)}
          data-testid={closeModalTestId}
          style={{ float: 'right', padding: '2px', margin: '0px' }}
        >
          <Close style={{ fontSize: '26px' }} />
        </Button>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        {!parseResult && !results && renderDropzone()}
        {!results && renderPreview()}
        {renderResults()}
        {apiError && (
          <Box data-testid={bulkAssignFileErrorTestId} style={{ color: colorPalette.warningRed, marginTop: 10 }}>
            {apiError}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', padding: 12 }}>
        <SecondaryActionButton onClick={handleClose} data-testid={bulkAssignCancelTestId}>
          {results ? t(`${i18Prefix}.close`) : t(`${i18Prefix}.cancel`)}
        </SecondaryActionButton>
        {!results && (
          <PrimaryActionButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid={bulkAssignSubmitTestId}
          >
            {submitting ? (
              <CircularProgress size={20} style={{ marginRight: 5, color: colorPalette.white }} />
            ) : undefined}
            {t(`${i18Prefix}.bulk_assign_submit`, { count: readyCount })}
          </PrimaryActionButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export {
  bulkAssignModalTestId,
  bulkAssignUploadTestId,
  bulkAssignSubmitTestId,
  bulkAssignCancelTestId,
  bulkAssignPublishTestId,
  bulkAssignFileErrorTestId,
  bulkAssignResultsTestId,
};
export default BulkAssignModalBody;
